import "./styles.css";
import {
  CONTACT_EMAIL,
  MORPH_DURATION_MS,
  ROTATION_INTERVAL_MS,
  type Locale,
} from "./config";
import { COPY, LEGAL_COPY } from "./content";
import { alternateLocale, buildMailto, detectLocale } from "./locale";
import { getMotionMode } from "./motion";
import {
  dialogFromHash,
  hashForDialog,
  type LegalDialogId,
} from "./navigation";
import { RotationController } from "./rotation";
import { ANIMATED_SIGNET_SLICES } from "./signet";

function queryElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error("Missing required element: " + selector);
  }
  return element;
}

function wordNodes(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const words = text.trim().split(/\s+/);

  words.forEach((word, index) => {
    const span = document.createElement("span");
    span.className = "morph-word";
    span.textContent = word;
    fragment.append(span);
    if (index < words.length - 1) {
      fragment.append(document.createTextNode(" "));
    }
  });

  return fragment;
}

function setWords(element: HTMLElement, text: string): void {
  element.replaceChildren(wordNodes(text));
}

function legalSection(
  locale: Locale,
  kind: LegalDialogId,
): HTMLElement {
  const legal = LEGAL_COPY[locale];
  const section = document.createElement("section");
  section.className = "legal-language";
  section.lang = locale;
  section.dataset.locale = locale;

  const heading = document.createElement("h2");
  heading.textContent =
    kind === "imprint" ? legal.imprintTitle : legal.privacyTitle;
  section.append(heading);

  const content = document.createElement("div");
  content.className = "legal-copy";
  content.innerHTML =
    kind === "imprint" ? legal.imprintHtml : legal.privacyHtml;
  section.append(content);
  return section;
}

function renderLegalDialog(
  dialog: HTMLDialogElement,
  kind: LegalDialogId,
  firstLocale: Locale,
): void {
  const heading = dialog.querySelector<HTMLElement>(".dialog-topbar > span");
  const body = dialog.querySelector<HTMLElement>(".dialog-body");
  if (!heading || !body) {
    return;
  }

  const primary = LEGAL_COPY[firstLocale];
  const secondary = LEGAL_COPY[alternateLocale(firstLocale)];
  heading.textContent =
    kind === "imprint"
      ? `${primary.imprintTitle} / ${secondary.imprintTitle}`
      : `${primary.privacyTitle} / ${secondary.privacyTitle}`;

  body.replaceChildren(
    legalSection(firstLocale, kind),
    legalSection(alternateLocale(firstLocale), kind),
  );
}

const initialLocale = detectLocale(navigator.languages);

const app = queryElement<HTMLDivElement>("#app");
app.innerHTML = [
  '<div class="site-shell">',
  '  <div class="motif motif--animated" aria-hidden="true">',
  '    <img class="motif-base" src="/assets/brand/signet.svg" alt="">',
  `    <div class="motif-slice motif-slice--one">${ANIMATED_SIGNET_SLICES.left}</div>`,
  `    <div class="motif-slice motif-slice--two">${ANIMATED_SIGNET_SLICES.middle}</div>`,
  `    <div class="motif-slice motif-slice--three">${ANIMATED_SIGNET_SLICES.right}</div>`,
  "  </div>",
  '  <header class="site-header staged-item">',
  '    <a class="brand" href="/" aria-label="Techmaker home">',
  "      <picture>",
  '        <source media="(prefers-color-scheme: dark)" srcset="/assets/brand/wordmark-light.svg">',
  '        <img src="/assets/brand/wordmark-dark.svg" width="433" height="55" alt="Techmaker">',
  "      </picture>",
  "    </a>",
  '    <div class="rotation-control-wrap">',
  '      <button class="rotation-control" type="button" aria-describedby="rotation-hint">',
  '        <span class="control-icon" aria-hidden="true"><span></span><span></span></span>',
  '        <span class="sr-only control-label"></span>',
  "      </button>",
  '      <span class="control-tooltip" id="rotation-hint" role="tooltip"></span>',
  "    </div>",
  "  </header>",
  '  <main class="main-content">',
  '    <article class="hero staged-item" id="hero">',
  '      <h1 class="hero-headline" id="headline"></h1>',
  '      <p class="hero-support" id="support"></p>',
  '      <div class="contact-block">',
  '        <a class="primary-cta" id="contact-cta"><span class="cta-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M3.75 6.75h16.5v10.5H3.75V6.75Z"/><path d="m4.5 7.5 7.5 5.25 7.5-5.25"/></svg></span><span id="cta-label"></span></a>',
  '        <a class="email-address" id="email-address"></a>',
  "      </div>",
  '      <p class="scope-note" id="scope"></p>',
  "    </article>",
  "  </main>",
  '  <footer class="site-footer staged-item">',
  "    <span>© 2026 Techmaker GmbH</span>",
  '    <nav class="legal-nav" aria-label="Legal">',
  '      <a href="#imprint" data-dialog="imprint" id="imprint-link"></a>',
  '      <a href="#privacy" data-dialog="privacy" id="privacy-link"></a>',
  "    </nav>",
  "  </footer>",
  "</div>",
  '<dialog class="legal-dialog" id="imprint-dialog" aria-labelledby="imprint-dialog-heading">',
  '  <div class="dialog-frame">',
  '    <div class="dialog-topbar"><span id="imprint-dialog-heading">Techmaker</span><form method="dialog"><button class="dialog-close" value="close"><span aria-hidden="true">×</span><span class="sr-only dialog-close-label"></span></button></form></div>',
  '    <div class="dialog-body"></div>',
  "  </div>",
  "</dialog>",
  '<dialog class="legal-dialog" id="privacy-dialog" aria-labelledby="privacy-dialog-heading">',
  '  <div class="dialog-frame">',
  '    <div class="dialog-topbar"><span id="privacy-dialog-heading">Techmaker</span><form method="dialog"><button class="dialog-close" value="close"><span aria-hidden="true">×</span><span class="sr-only dialog-close-label"></span></button></form></div>',
  '    <div class="dialog-body"></div>',
  "  </div>",
  "</dialog>",
].join("");

const hero = queryElement<HTMLElement>("#hero");
const headline = queryElement<HTMLElement>("#headline");
const support = queryElement<HTMLElement>("#support");
const cta = queryElement<HTMLAnchorElement>("#contact-cta");
const ctaLabel = queryElement<HTMLElement>("#cta-label");
const emailAddress = queryElement<HTMLAnchorElement>("#email-address");
const scope = queryElement<HTMLElement>("#scope");
const pauseButton = queryElement<HTMLButtonElement>(".rotation-control");
const pauseLabel = queryElement<HTMLElement>(".control-label");
const pauseHint = queryElement<HTMLElement>("#rotation-hint");
const imprintLink = queryElement<HTMLAnchorElement>("#imprint-link");
const privacyLink = queryElement<HTMLAnchorElement>("#privacy-link");
const imprintDialog = queryElement<HTMLDialogElement>("#imprint-dialog");
const privacyDialog = queryElement<HTMLDialogElement>("#privacy-dialog");
const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

let locale = initialLocale;
let morphing = false;

function updateControlCopy(): void {
  const copy = COPY[locale];
  const label = rotation.isManuallyPaused() ? copy.resume : copy.pause;
  pauseButton.setAttribute("aria-label", label);
  pauseButton.setAttribute(
    "aria-pressed",
    rotation.isManuallyPaused() ? "true" : "false",
  );
  pauseButton.dataset.state = rotation.isManuallyPaused() ? "paused" : "playing";
  pauseLabel.textContent = label;
  pauseHint.textContent = copy.pauseHint;
}

function renderCopy(nextLocale: Locale): void {
  locale = nextLocale;
  const copy = COPY[locale];
  document.documentElement.lang = locale;
  hero.lang = locale;

  setWords(headline, copy.headline);
  setWords(support, copy.support);
  setWords(ctaLabel, copy.cta);
  setWords(scope, copy.scope);

  cta.href = buildMailto(CONTACT_EMAIL, copy.emailSubject);
  cta.setAttribute("aria-label", copy.cta + " — " + CONTACT_EMAIL);
  emailAddress.href = "mailto:" + CONTACT_EMAIL;
  emailAddress.textContent = CONTACT_EMAIL;
  imprintLink.textContent = copy.imprint;
  privacyLink.textContent = copy.privacy;
  document.querySelectorAll<HTMLElement>(".dialog-close-label").forEach((label) => {
    label.textContent = copy.close;
  });
  updateControlCopy();
}

async function animateWords(
  direction: "out" | "in",
): Promise<void> {
  const words = Array.from(hero.querySelectorAll<HTMLElement>(".morph-word"));
  const animations = words.map((word, index) => {
    const delay = Math.min(index * 14, 280);
    const keyframes: Keyframe[] =
      direction === "out"
        ? [
            { opacity: 1, transform: "translateY(0) rotateX(0deg)", clipPath: "inset(0 0 0 0)" },
            { opacity: 0, transform: "translateY(-0.35em) rotateX(72deg)", clipPath: "inset(0 0 100% 0)" },
          ]
        : [
            { opacity: 0, transform: "translateY(0.35em) rotateX(-72deg)", clipPath: "inset(100% 0 0 0)" },
            { opacity: 1, transform: "translateY(0) rotateX(0deg)", clipPath: "inset(0 0 0 0)" },
          ];

    return word.animate(keyframes, {
      duration: direction === "out" ? 220 : 340,
      delay,
      easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      fill: "both",
    }).finished;
  });
  await Promise.allSettled(animations);
}

async function morphTo(nextLocale: Locale): Promise<void> {
  if (morphing || nextLocale === locale) {
    return;
  }

  morphing = true;
  hero.dataset.morphing = "true";
  const motionMode = getMotionMode(reducedMotionQuery.matches);

  if (motionMode === "reduced") {
    await hero
      .animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 80,
        easing: "linear",
        fill: "both",
      })
      .finished;
    renderCopy(nextLocale);
    await hero
      .animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 120,
        easing: "linear",
        fill: "both",
      })
      .finished;
  } else {
    await animateWords("out");
    renderCopy(nextLocale);
    await animateWords("in");
  }

  delete hero.dataset.morphing;
  morphing = false;
}

const rotation = new RotationController(ROTATION_INTERVAL_MS, () => {
  void morphTo(alternateLocale(locale));
});

renderCopy(initialLocale);
renderLegalDialog(imprintDialog, "imprint", initialLocale);
renderLegalDialog(privacyDialog, "privacy", initialLocale);
rotation.start();

pauseButton.addEventListener("click", () => {
  rotation.toggleManual();
  updateControlCopy();
});

document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    rotation.pause("visibility");
  } else {
    rotation.resume("visibility");
  }
});

function dialogById(id: LegalDialogId): HTMLDialogElement {
  return id === "imprint" ? imprintDialog : privacyDialog;
}

function openDialog(id: LegalDialogId, updateHash = true): void {
  const dialog = dialogById(id);
  if (dialog.open) {
    return;
  }

  renderLegalDialog(dialog, id, initialLocale);
  rotation.pause("dialog");
  dialog.showModal();

  if (updateHash) {
    history.replaceState(null, "", hashForDialog(id));
  }
}

document.querySelectorAll<HTMLElement>("[data-dialog]").forEach((trigger) => {
  trigger.addEventListener("click", (event) => {
    event.preventDefault();
    const id = trigger.dataset.dialog as LegalDialogId;
    openDialog(id);
  });
});

[imprintDialog, privacyDialog].forEach((dialog) => {
  dialog.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      dialog.close();
    }
  });

  dialog.addEventListener("close", () => {
    rotation.resume("dialog");
    if (dialogFromHash(window.location.hash)) {
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  });

  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) {
      dialog.close();
    }
  });
});

window.addEventListener("hashchange", () => {
  const id = dialogFromHash(window.location.hash);
  if (id) {
    openDialog(id, false);
  }
});

const initialDialog = dialogFromHash(window.location.hash);
if (initialDialog) {
  openDialog(initialDialog, false);
}

requestAnimationFrame(() => {
  document.body.classList.add("is-ready");
});

window.setTimeout(() => {
  document.body.classList.add("entry-complete");
}, MORPH_DURATION_MS);
